/**
 * Stale-status helpers (IA §9.1 / Plan 05 Task 5.6).
 *
 * The Langfuse sync worker writes `synced_at` on every trace. We derive a
 * four-band freshness signal from the age of the most recent sync:
 *
 *   < 15 min          → green      (healthy)
 *   < 30 min          → yellow     (warning — sync slightly behind)
 *   < 2 h             → red        (degraded)
 *   ≥ 2 h             → dark-red   (dead)
 *   null (never)      → grey       (unknown)
 *
 * Both the top-nav `<StaleStatusDot>` and the Overview-tab
 * `<StaleStatusCard>` call `ageToColor()` so the single source of truth
 * stays here. Keep this file free of React imports — it's loaded by the
 * unit tests without the component harness.
 */

export type StaleColor = 'grey' | 'green' | 'yellow' | 'red' | 'dark-red';

const MIN = 60_000;
const HOUR = 60 * MIN;

/**
 * Map an age in milliseconds to the stale-status colour.
 * `null` means "never synced / unknown" — rendered grey.
 */
export function ageToColor(ageMs: number | null): StaleColor {
  if (ageMs === null || ageMs === undefined) return 'grey';
  if (ageMs < 15 * MIN) return 'green';
  if (ageMs < 30 * MIN) return 'yellow';
  if (ageMs < 2 * HOUR) return 'red';
  return 'dark-red';
}

/** Human-readable age string suitable for a tooltip or card body. */
export function ageToHuman(ageMs: number | null): string {
  if (ageMs === null || ageMs === undefined) return 'never synced';
  if (ageMs < MIN) return 'just now';
  if (ageMs < HOUR) return `${Math.floor(ageMs / MIN)} min ago`;
  if (ageMs < 24 * HOUR) return `${Math.floor(ageMs / HOUR)} h ago`;
  return `${Math.floor(ageMs / (24 * HOUR))} d ago`;
}

/** Map colour → Tailwind bg class. Kept next to the helper to avoid
 *  duplicating the palette between the dot and the card. */
export const COLOR_CLASS: Record<StaleColor, string> = {
  grey: 'bg-gray-400',
  green: 'bg-green-500',
  yellow: 'bg-yellow-400',
  red: 'bg-red-500',
  'dark-red': 'bg-red-800',
};
